import { faCheck, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useState } from "react";
import { ThirdPartyEnabledEnum } from "../../apiClient/generated";
import { useDataProvidersApi, useNotificationsApi } from "../../hooks";
import { CustomSwitch } from "../../ui/CustomSwitch";
import { LoadingIndicator } from "../MapV2/ui/LoadingIndicator";

interface FrequencyRadioProps {
    value: string;
    selectedValue?: string;
    name: string;
    label: string;
    onChange: (value: string) => void;
    disabled?: boolean;
}

const FrequencyRadio = (props: FrequencyRadioProps) => (
    <label className="flex cursor-pointer justify-center">
        <span className="sr-only">{props.label}</span>
        <input
            type="radio"
            name={props.name}
            value={props.value}
            checked={props.selectedValue === props.value}
            disabled={props.disabled}
            onChange={() => props.onChange(props.value)}
            className="h-5 w-5 cursor-pointer border-ae-blue-900 accent-ae-blue-900 checked:border-ae-blue-900 checked:bg-ae-blue-900 disabled:cursor-default"
        />
    </label>
);

interface AlertRowProps {
    title: string;
    description: string;
    value?: string;
    group?: string | number | null;
    onChange: (value: string) => void;
    onGroupChange: (value: string) => void;
    groups: { id: number; name: string }[];
    disabled: boolean;
    supportsDigest?: boolean;
    children?: ReactNode;
}

const AlertRow = (props: AlertRowProps) => (
    <div className="border-b border-slate-100 px-6 py-4 last:border-b-0">
        <div className="grid grid-cols-[minmax(18rem,1fr)_10rem_10rem_8rem_17rem] items-center gap-x-3">
            <div>
                <h3 className="text-sm font-semibold text-slate-900">
                    {props.title}
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                    {props.description}
                </p>
            </div>
            <FrequencyRadio
                name={props.title}
                label="Every detection"
                value={ThirdPartyEnabledEnum.All}
                selectedValue={props.value}
                onChange={props.onChange}
                disabled={props.disabled}
            />
            {props.supportsDigest ? (
                <FrequencyRadio
                    name={props.title}
                    label="Digest"
                    value={ThirdPartyEnabledEnum.BatchDigest}
                    selectedValue={props.value}
                    onChange={props.onChange}
                    disabled={props.disabled}
                />
            ) : (
                <span className="text-center text-sm text-slate-400">−</span>
            )}
            <FrequencyRadio
                name={props.title}
                label="Off"
                value={ThirdPartyEnabledEnum.None}
                selectedValue={props.value}
                onChange={props.onChange}
                disabled={props.disabled}
            />
            <select
                aria-label={`${props.title} notification group`}
                className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-800 outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 disabled:cursor-not-allowed"
                value={props.group ?? ""}
                onChange={(event) => props.onGroupChange(event.target.value)}
                disabled={props.disabled}
            >
                <option value="" disabled>
                    Select a group
                </option>
                {props.groups.map((group) => (
                    <option key={group.id} value={group.id}>
                        {group.name}
                    </option>
                ))}
            </select>
        </div>
        {props.children}
    </div>
);

interface EmissionSettingsProps {
    company?: number;
    disabled?: boolean;
    onSaveStateChange?: (saved: boolean) => void;
    onSaveErrorChange?: (failed: boolean) => void;
}

export const EmissionSettings = (props: EmissionSettingsProps) => {
    const apiClient = useNotificationsApi();
    const dataProvidersApiClient = useDataProvidersApi();
    const queryClient = useQueryClient();
    const [savingFields, setSavingFields] = useState<string[]>([]);
    const isSavingField = (field: string) => savingFields.includes(field);

    // Fetch additional data
    const enabledDataProvidersQuery = useQuery({
        queryKey: ["enabledDataProviders", props.company],
        queryFn: async () => {
            return await dataProvidersApiClient.dataProvidersList({
                isEnabled: true,
                company: props.company,
            });
        },
        staleTime: Infinity,
    });

    const notificationGroupsQuery = useQuery({
        queryKey: ["notificationGroup", props.company],
        queryFn: async () =>
            await apiClient.notificationsGroupsList({
                owner: props.company,
                pageSize: 100,
            }),
    });

    // Notification settings
    const notificationSettingsQuery = useQuery({
        queryKey: ["emissionNotificationSettings", props.company],
        queryFn: async () => {
            const response = await apiClient.notificationsEmissionsList({
                owner: props.company,
                pageSize: 1,
            });
            return response.results[0];
        },
    });

    const saveChangesMutation = useMutation({
        mutationFn: async ({
            field,
            value,
        }: {
            field: string;
            value: string | number | boolean | string[];
        }) =>
            await apiClient.notificationsEmissionsPartialUpdate({
                id: notificationSettingsQuery.data?.id,
                patchedEmissionNotificationSettingsRequest: {
                    [field]: value,
                },
            }),
        onMutate: ({ field }) => {
            props.onSaveStateChange?.(false);
            props.onSaveErrorChange?.(false);
            setSavingFields((fields) =>
                fields.includes(field) ? fields : [...fields, field],
            );
        },
        onSuccess: () => {
            props.onSaveStateChange?.(true);
            props.onSaveErrorChange?.(false);
            queryClient.invalidateQueries({
                queryKey: ["emissionNotificationSettings", props.company],
            });
        },
        onError: () => {
            props.onSaveStateChange?.(false);
            props.onSaveErrorChange?.(true);
        },
        onSettled: (_, __, { field }) => {
            setSavingFields((fields) =>
                fields.filter((savingField) => savingField !== field),
            );
        },
    });

    const saveChanges = (
        field: string,
        value: string | number | boolean | string[],
    ) => {
        if (!props.disabled && !isSavingField(field)) {
            saveChangesMutation.mutate({ field, value });
        }
    };

    const toggleProvider = (providerId: string) => {
        const excludedProviders =
            notificationSettingsQuery.data?.thirdPartyExcludedProviders;
        if (!excludedProviders) return;
        let newValues = [...excludedProviders];

        if (newValues.includes(providerId)) {
            newValues = newValues.filter((i) => i !== providerId);
        } else {
            newValues.push(providerId);
        }

        saveChanges("thirdPartyExcludedProviders", newValues);
    };

    if (
        notificationSettingsQuery.isLoading ||
        notificationGroupsQuery.isLoading ||
        enabledDataProvidersQuery.isLoading
    ) {
        return <LoadingIndicator />;
    }

    return (
        <div className="text-sm">
            <div className="overflow-x-auto">
                <div className="min-w-[850px]">
                    <div className="grid grid-cols-[minmax(18rem,1fr)_10rem_10rem_8rem_17rem] items-center gap-x-3 bg-slate-50 px-6 py-3 text-sm font-semibold text-ae-blue-900">
                        <span>Alert</span>
                        <span className="text-center">Every detection</span>
                        <span className="text-center">Digest</span>
                        <span className="text-center">Off</span>
                        <span>Send to</span>
                    </div>

                    <AlertRow
                        title="Company monitoring"
                        description="Detections from your own monitoring."
                        value={
                            notificationSettingsQuery.data?.selfReportedEnabled
                        }
                        group={
                            notificationSettingsQuery.data?.selfReportedGroup
                        }
                        groups={notificationGroupsQuery.data?.results ?? []}
                        onChange={(value) =>
                            saveChanges("selfReportedEnabled", value)
                        }
                        onGroupChange={(value) =>
                            saveChanges("selfReportedGroup", Number(value))
                        }
                        disabled={
                            props.disabled ||
                            isSavingField("selfReportedEnabled") ||
                            isSavingField("selfReportedGroup")
                        }
                        supportsDigest
                    />

                    <AlertRow
                        title="Third-party"
                        description="Detections reported by satellite and aerial providers."
                        value={
                            notificationSettingsQuery.data?.thirdPartyEnabled
                        }
                        group={notificationSettingsQuery.data?.thirdPartyGroup}
                        groups={notificationGroupsQuery.data?.results ?? []}
                        onChange={(value) =>
                            saveChanges("thirdPartyEnabled", value)
                        }
                        onGroupChange={(value) =>
                            saveChanges("thirdPartyGroup", Number(value))
                        }
                        disabled={
                            props.disabled ||
                            isSavingField("thirdPartyEnabled") ||
                            isSavingField("thirdPartyGroup")
                        }
                        supportsDigest
                    >
                        <div className="mt-2 inline-flex items-center gap-3 px-4 py-3 text-sm text-slate-800">
                            <CustomSwitch
                                checked={
                                    notificationSettingsQuery.data
                                        ?.notifyOnUnmatchedEmissions
                                }
                                onChange={(value) =>
                                    saveChanges(
                                        "notifyOnUnmatchedEmissions",
                                        value,
                                    )
                                }
                                size="lg"
                                colorScheme="notification"
                                disabled={
                                    props.disabled ||
                                    isSavingField("notifyOnUnmatchedEmissions")
                                }
                            />
                            Notify when 3rd party providers attribute emissions
                            to my company
                        </div>
                        <hr />
                        <fieldset className="mt-4 flex flex-wrap items-center gap-4 py-3">
                            <legend className="sr-only">Providers</legend>
                            <span className="flex items-center gap-1 font-semibold text-slate-500">
                                Providers (click to change):
                            </span>
                            {enabledDataProvidersQuery.data?.results.map(
                                (provider) => {
                                    const isIncluded =
                                        !notificationSettingsQuery.data?.thirdPartyExcludedProviders.includes(
                                            provider.id,
                                        );
                                    const disabled =
                                        props.disabled ||
                                        isSavingField(
                                            "thirdPartyExcludedProviders",
                                        );

                                    return (
                                        <label
                                            key={provider.id}
                                            className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                                                isIncluded
                                                    ? "border-ae-blue-900 bg-indigo-50 text-ae-blue-900"
                                                    : "border-red-200 bg-red-50 text-red-700"
                                            } ${
                                                disabled
                                                    ? "cursor-default opacity-60"
                                                    : "cursor-pointer"
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                className="sr-only"
                                                checked={isIncluded}
                                                onChange={() =>
                                                    toggleProvider(provider.id)
                                                }
                                                disabled={disabled}
                                            />
                                            {isIncluded ? (
                                                <FontAwesomeIcon
                                                    icon={faCheck}
                                                    className="h-3 w-3"
                                                    aria-hidden="true"
                                                />
                                            ) : (
                                                <FontAwesomeIcon
                                                    icon={faXmark}
                                                    className="h-3 w-3"
                                                    aria-hidden="true"
                                                />
                                            )}
                                            {provider.name}
                                        </label>
                                    );
                                },
                            )}
                        </fieldset>
                    </AlertRow>
                </div>
            </div>
        </div>
    );
};
